import { Plus, Trash2, ChevronUp, ChevronDown, FileText } from 'lucide-react';
import { useStore, activeSheet } from '../store';

const DOC_CATEGORIES = [
  '',
  'Installation',
  'Maintenance',
  'Operation',
  'Troubleshooting',
  'Safety',
  'Commissioning',
  'Reference',
  'Other',
];

export function DocumentSheet() {
  const sheet = useStore(activeSheet);
  const addSection = useStore((s) => s.addSection);
  const updateSection = useStore((s) => s.updateSection);
  const removeSection = useStore((s) => s.removeSection);
  const moveSection = useStore((s) => s.moveSection);
  const setDocumentCategory = useStore((s) => s.setDocumentCategory);

  const sections = sheet.sections ?? [];
  const category = sheet.documentCategory ?? '';

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-canvas)',
        overflow: 'hidden',
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 16px',
          background: 'var(--panel)',
          borderBottom: '1px solid var(--panel-border)',
        }}
      >
        <button onClick={() => addSection()} style={primaryBtn} title="Add new section">
          <Plus size={14} /> Add section
        </button>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--text-muted)',
            fontSize: 12,
          }}
        >
          <FileText size={13} />
          <span>Category</span>
        </div>
        <select
          value={category}
          onChange={(e) => setDocumentCategory(e.target.value)}
          style={{
            border: '1px solid var(--panel-border)',
            borderRadius: 'var(--radius-sm)',
            padding: '5px 8px',
            fontSize: 12,
            background: 'var(--panel)',
            color: 'var(--text)',
            outline: 'none',
          }}
        >
          {DOC_CATEGORIES.map((c) => (
            <option key={c || '__none__'} value={c}>
              {c || '— Uncategorized —'}
            </option>
          ))}
        </select>

        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {sections.length} section{sections.length === 1 ? '' : 's'}
        </div>
      </div>

      {/* Sections list */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 880,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          {sections.length === 0 && (
            <div
              style={{
                color: 'var(--text-muted)',
                fontSize: 13,
                textAlign: 'center',
                padding: '60px 20px',
                border: '1px dashed var(--panel-border)',
                borderRadius: 'var(--radius)',
                background: 'var(--panel)',
              }}
            >
              <FileText size={32} style={{ opacity: 0.4, marginBottom: 8 }} />
              <div style={{ fontWeight: 600, marginBottom: 4 }}>This document is empty</div>
              <div>Click “+ Add section” to start writing.</div>
            </div>
          )}

          {sections.map((section, idx) => (
            <div
              key={section.id}
              style={{
                background: 'var(--panel)',
                border: '1px solid var(--panel-border)',
                borderRadius: 'var(--radius)',
                padding: '14px 16px',
                boxShadow: 'var(--shadow-sm)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                <input
                  value={section.heading}
                  onChange={(e) => updateSection(section.id, { heading: e.target.value })}
                  placeholder={`Section ${idx + 1}`}
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 16,
                    fontWeight: 700,
                    color: 'var(--text)',
                    outline: 'none',
                    padding: '4px 0',
                  }}
                />
                <button
                  onClick={() => moveSection(section.id, -1)}
                  disabled={idx === 0}
                  style={iconBtn}
                  title="Move up"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  onClick={() => moveSection(section.id, 1)}
                  disabled={idx === sections.length - 1}
                  style={iconBtn}
                  title="Move down"
                >
                  <ChevronDown size={14} />
                </button>
                <button
                  onClick={() => removeSection(section.id)}
                  style={{ ...iconBtn, color: 'var(--danger)' }}
                  title="Delete section"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <textarea
                value={section.body}
                onChange={(e) => updateSection(section.id, { body: e.target.value })}
                placeholder="Write the body of this section here. Plain text — multi-line supported."
                rows={Math.max(4, Math.min(24, section.body.split('\n').length + 1))}
                style={{
                  width: '100%',
                  border: '1px solid var(--panel-border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '8px 10px',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  background: 'var(--bg-canvas)',
                  color: 'var(--text)',
                  outline: 'none',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  lineHeight: 1.55,
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 12px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

const iconBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  background: 'transparent',
  border: '1px solid var(--panel-border)',
  borderRadius: 4,
  color: 'var(--text-muted)',
  cursor: 'pointer',
};
